import { useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import {
  getBusinessBillPaymentsTransactions,
  getBusinessTransactionsAnalytics,
  getBusinessTransfersTransactions,
} from "api/business-transactions";
import {
  IBusinessBillPaymentsTransactionsResult,
  IBusinessTransactionsAnalytics,
  IBusinessTransfersTransactionsResult,
  IGetBusinessBillPaymentsTransactionsPayload,
  IGetBusinessTransactionsAnalyticsPayload,
  IGetBusinessTransfersTransactionsPayload,
} from "api/business-transactions/types";
import { TTransactionType } from "features/businesses/business-details/content/transactions/types";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { replaceIdParamsInRouteWithValue } from "utils";
import { ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { customErrorToast } from "ui/custom-toast";
import { DEFAULT_PAGINATION_DATA } from "ui/pagination";
import { Tabs } from "ui/tabs";
import { ITabsData } from "ui/tabs/types";

import { BillPaymentsTransactionsTable } from "../business-transactions-list-table/BillPaymentsTransactionsTable";
import { TransfersTransactionsTable } from "../business-transactions-list-table/TransfersTransactionsTable";
import { DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS } from "../business-transactions-list-table/const";

export enum TransactionsSubRouteName {
  TRANSFERS = "transfers",
  BILLS_PAYMENTS = "bills-payments",
  ON_DEMAND_PAY = "odp",
}

const tabsContentAsLinks: ITabsData[] = [
  {
    value: TransactionsSubRouteName.TRANSFERS,
    content: <></>,
  },
  {
    value: TransactionsSubRouteName.BILLS_PAYMENTS,
    content: <></>,
  },
];

const DEFAULT_TRANSACTION_ANALYTICS: IBusinessTransactionsAnalytics = {
  totalAmount: ZERO,
  totalCount: ZERO,
  successfulAmount: ZERO,
  successfulCount: ZERO,
  failedAmount: ZERO,
  failedCount: ZERO,
  successPercentage: ZERO,
  failedPercentage: ZERO,
  averageAmount: ZERO,
};

export const BusinessTransactionsContent = () => {
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const status = searchParams.get("status");
  const type = searchParams.get("type");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const search = searchParams.get("search");
  const refreshTable = searchParams.get("refreshTable");
  const filterRetryCount = searchParams.get("filterRetryCount");
  const [
    businessTransfersTransactionsResult,
    setBusinessTransfersTransactionsResult,
  ] = useState<IBusinessTransfersTransactionsResult>({
    data: [],
    ...DEFAULT_PAGINATION_DATA,
  });

  const [isLoadingTransfersTransactions, setIsLoadingTransfersTransactions] =
    useState(false);
  const [
    isLoadingBillPaymentsTransactions,
    setIsLoadingBillPaymentsTransactions,
  ] = useState(false);
  const [
    businessBillPaymentsTransactionsResult,
    setBusinessBillPaymentsTransactionsResult,
  ] = useState<IBusinessBillPaymentsTransactionsResult>({
    data: [],
    ...DEFAULT_PAGINATION_DATA,
  });
  const { transactionType } = useParams<{
    transactionType: TransactionsSubRouteName;
  }>();
  const navigate = useNavigate();
  const [businessTransactionAnalytics, setBusinessTransactionAnalytics] =
    useState<IBusinessTransactionsAnalytics>(DEFAULT_TRANSACTION_ANALYTICS);
  const [
    isLoadingBusinessTransactionAnalytics,
    setIsLoadingBusinessTransactionAnalytics,
  ] = useState(false);

  const handleTabSwitch = (tab: TransactionsSubRouteName) => {
    const routeToNavigateTo = replaceIdParamsInRouteWithValue({
      route: ROUTES.BUSINESSES_TRANSACTIONS_PAGE,
      idParamsName: ":transactionType",
      value: tab,
    });
    navigate(routeToNavigateTo);
  };

  const tabHeadersAsLinks: ITabsData[] = [
    {
      value: TransactionsSubRouteName.TRANSFERS,
      content: (
        <Box
          onClick={() => handleTabSwitch(TransactionsSubRouteName.TRANSFERS)}
        >
          Transfers
        </Box>
      ),
    },
    {
      value: TransactionsSubRouteName.BILLS_PAYMENTS,
      content: (
        <Box
          onClick={() =>
            handleTabSwitch(TransactionsSubRouteName.BILLS_PAYMENTS)
          }
        >
          Bills Payments
        </Box>
      ),
    },
  ];

  const handleGetBusinessTransfersTransactions = async (
    payload: IGetBusinessTransfersTransactionsPayload
  ) => {
    setIsLoadingTransfersTransactions(true);
    try {
      const { data, error, message } =
        await getBusinessTransfersTransactions(payload);
      if (error) {
        customErrorToast({ message });
      } else {
        setBusinessTransfersTransactionsResult(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingTransfersTransactions(false);
  };

  const handleGetBusinessBillPaymentsTransactions = async (
    payload: IGetBusinessBillPaymentsTransactionsPayload
  ) => {
    setIsLoadingBillPaymentsTransactions(true);
    try {
      const { data, error, message } =
        await getBusinessBillPaymentsTransactions(payload);
      if (error) {
        customErrorToast({ message });
      } else {
        setBusinessBillPaymentsTransactionsResult(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingBillPaymentsTransactions(false);
  };

  const handleGetBusinessTransactionsAnalytics = async (
    payload: IGetBusinessTransactionsAnalyticsPayload
  ) => {
    setIsLoadingBusinessTransactionAnalytics(true);
    setBusinessTransactionAnalytics(DEFAULT_TRANSACTION_ANALYTICS);
    try {
      const { data, error, message } =
        await getBusinessTransactionsAnalytics(payload);
      if (error) {
        customErrorToast({ message });
      } else {
        setBusinessTransactionAnalytics(data);
      }
    } catch (error) {
      console.error("Error getting analytics::", error);
    }
    setIsLoadingBusinessTransactionAnalytics(false);
  };

  useEffect(() => {
    if (!transactionType) return;
    const payload: IGetBusinessTransfersTransactionsPayload = {
      page: Number(
        page || DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page
      ),
      limit: Number(
        limit || DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit
      ),
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (status) {
      const transactionStatuses = status.split(",");
      if (!transactionStatuses.includes("all")) {
        payload["status"] = transactionStatuses.join(",");
      }
    }

    if (type) {
      const transactionTypes = type.split(",");
      const containsCredit = transactionTypes.includes("credit");
      const containsDebit = transactionTypes.includes("debit");
      if (
        !transactionTypes.includes("all") &&
        containsCredit !== containsDebit
      ) {
        const [typeToSend] = transactionTypes;
        payload["type"] = typeToSend as TTransactionType;
      }
    }

    if (search) {
      payload["search"] = search;
      payload["searchBy"] = "description,reference";
    }

    switch (transactionType as TransactionsSubRouteName) {
      case TransactionsSubRouteName.TRANSFERS:
        void handleGetBusinessTransfersTransactions(payload);
        break;
      case TransactionsSubRouteName.BILLS_PAYMENTS:
        void handleGetBusinessBillPaymentsTransactions(payload);
        break;
      default:
        break;
    }
  }, [
    transactionType,
    page,
    limit,
    status,
    type,
    startDate,
    endDate,
    search,
    refreshTable,
    filterRetryCount,
  ]);

  useEffect(() => {
    if (!transactionType) return;

    if (
      ![
        TransactionsSubRouteName.BILLS_PAYMENTS,
        TransactionsSubRouteName.TRANSFERS,
      ].includes(transactionType)
    ) {
      navigate(ROUTES.NOT_FOUND_PAGE);
      return;
    }

    switch (transactionType as TransactionsSubRouteName) {
      case TransactionsSubRouteName.TRANSFERS:
        void handleGetBusinessTransactionsAnalytics({
          transactionType: "transfer",
        });
        break;
      case TransactionsSubRouteName.BILLS_PAYMENTS:
        void handleGetBusinessTransactionsAnalytics({
          transactionType: "bill_payment",
        });
        break;
      default:
        break;
    }
  }, [transactionType]);

  return (
    <div>
      <Tabs
        headers={tabHeadersAsLinks}
        contentList={tabsContentAsLinks}
        tabsListHeaderCss={{ gap: "24px" }}
        renderTabNavAsLink
        defaultTabValue={transactionType}
      />
      <Box className="mt-[-15px]">
        <Maybe
          condition={transactionType === TransactionsSubRouteName.TRANSFERS}
        >
          <TransfersTransactionsTable
            transfersTransactionsResult={businessTransfersTransactionsResult}
            isLoading={isLoadingTransfersTransactions}
            handleGetTransfersTransactions={
              handleGetBusinessTransfersTransactions
            }
            isLoadingTransfersTransactionAnalytics={
              isLoadingBusinessTransactionAnalytics
            }
            transfersTransactionAnalytics={businessTransactionAnalytics}
          />
        </Maybe>
        <Maybe
          condition={
            transactionType === TransactionsSubRouteName.BILLS_PAYMENTS
          }
        >
          <BillPaymentsTransactionsTable
            billPaymentsTransactionsResult={
              businessBillPaymentsTransactionsResult
            }
            isLoading={isLoadingBillPaymentsTransactions}
            handleGetBillPaymentsTransactions={
              handleGetBusinessBillPaymentsTransactions
            }
            isLoadingBillPaymentsTransactionAnalytics={
              isLoadingBusinessTransactionAnalytics
            }
            billsPaymentsTransactionAnalytics={businessTransactionAnalytics}
          />
        </Maybe>
      </Box>
    </div>
  );
};
